import moment from 'moment'
import Papa from 'papaparse'

// Parses an organiser-supplied participant/entry list (CSV or TSV) into the
// canonical rows POSTed to /events/:eventID/participants. Headers are matched
// tolerantly so the common race-entry exports map without a template edit.

// canonical field -> accepted headers, compared after normalizeHeader()
const HEADER_ALIASES = {
  number: [
    'race bib number',
    'bib',
    'bib number',
    'bib no',
    'race no',
    'race number',
    'runner number',
    'participant number',
    'number',
  ],
  firstName: ['first name', 'firstname', 'forename'],
  lastName: ['last name', 'lastname', 'surname'],
  email: ['email', 'email address'],
  gender: ['gender', 'sex'],
  dob: ['date of birth', 'dob'],
  addressLine1: ['address line 1', 'address 1', 'address'],
  addressLine2: ['address line 2', 'address 2'],
  addressLine3: ['address line 3', 'address 3'],
  county: ['county'],
  postCode: ['eircode', 'postcode', 'post code', 'zip'],
  contactNumber: ['mobile', 'mobile number', 'phone', 'phone number', 'contact number'],
  club: ['club'],
  nonClub: ['nonclub', 'non club'],
  emergencyContactName: ['emergency contact name', 'ice name'],
  emergencyContactNumber: [
    'emergency contact phone',
    'emergency contact number',
    'ice phone',
  ],
  medicalNotes: ['medical notes', 'medical conditions', 'medical'],
  allergies: ['allergies'],
  medications: ['medications', 'medication'],
}

const DOB_FORMATS = ['DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY']

export const CHUNK_SIZE = 500

const normalizeHeader = (header) =>
  String(header || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

// header name in the file -> canonical field (unknown headers dropped)
export const buildHeaderMap = (fields = []) => {
  const map = {}
  fields.forEach((field) => {
    const normalized = normalizeHeader(field)
    const canonical = Object.keys(HEADER_ALIASES).find((key) =>
      HEADER_ALIASES[key].includes(normalized),
    )
    if (canonical && !Object.values(map).includes(canonical)) {
      map[field] = canonical
    }
  })
  return map
}

const isEmptyRow = (row) =>
  Object.values(row).every(
    (value) => value === null || value === undefined || String(value).trim() === '',
  )

export const parseParticipantCsv = (file) =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim().replace(/^﻿/, ''),
      complete: (results) => {
        const errors = []
        const warnings = []
        const headerMap = buildHeaderMap(results.meta.fields)

        if (!Object.values(headerMap).includes('number')) {
          errors.push(
            'No bib/race number column found. Expected a header such as "Race Bib number", "Bib" or "Runner Number".',
          )
          return resolve({ rows: [], warnings, errors })
        }

        const seenNumbers = new Map() // normalized bib -> first rowNumber
        const rows = []

        results.data.forEach((rawRow, idx) => {
          const rowNumber = idx + 2 // header row is 1
          if (isEmptyRow(rawRow)) return

          const row = {}
          Object.entries(headerMap).forEach(([header, field]) => {
            const value = String(rawRow[header] ?? '').trim()
            if (value) row[field] = value
          })

          // Unaffiliated entrants arrive in a NonClub column instead
          if (!row.club && row.nonClub) row.club = row.nonClub
          delete row.nonClub

          if (!row.number) {
            warnings.push(`Row ${rowNumber}: no bib number — skipped`)
            return
          }

          const normalized = row.number.toUpperCase()
          if (seenNumbers.has(normalized)) {
            warnings.push(
              `Row ${rowNumber}: duplicate bib ${row.number} — kept row ${seenNumbers.get(normalized)}`,
            )
            return
          }
          seenNumbers.set(normalized, rowNumber)

          if (row.dob) {
            const parsed = moment(row.dob, DOB_FORMATS, true)
            if (parsed.isValid()) {
              row.dob = parsed.toISOString()
            } else {
              warnings.push(
                `Row ${rowNumber}: could not read date of birth "${row.dob}" — imported without it`,
              )
              delete row.dob
            }
          }

          rows.push(row)
        })

        if (rows.length === 0) {
          errors.push('No usable rows found in the file.')
        }

        resolve({ rows, warnings, errors })
      },
      error: (err) => reject(err),
    })
  })

// Header + example row for the downloadable template. Generated client-side
// (see helpers/downloadCsv.js) — a static file under public/ is never served.
export const TEMPLATE_ROWS = [
  [
    'Race Bib number',
    'First Name',
    'Last Name',
    'Email',
    'Gender',
    'Date of Birth',
    'Address Line 1',
    'Address Line 2',
    'Address Line 3',
    'County',
    'Eircode',
    'Mobile',
    'Club',
    'NonClub',
    'Emergency Contact Name',
    'Emergency Contact Phone',
    'Medical Notes',
    'Allergies',
    'Medications',
  ],
  [
    '101',
    'Jane',
    'Doe',
    'jane@example.com',
    'F',
    '14/02/1988',
    '1 Main Street',
    'Ballybrack',
    'Killarney',
    'Kerry',
    'V93 XY12',
    '0871234567',
    'Killarney AC',
    '',
    'John Doe',
    '0869876543',
    'Asthma',
    'Penicillin',
    'Salbutamol inhaler',
  ],
]

export const buildTemplateCsv = () => Papa.unparse(TEMPLATE_ROWS)

export const chunkRows = (rows, size = CHUNK_SIZE) => {
  const chunks = []
  for (let i = 0; i < rows.length; i += size) {
    chunks.push(rows.slice(i, i + size))
  }
  return chunks
}
